import React, { useCallback, useEffect, useState } from 'react';
import { FaBatteryEmpty, FaBatteryFull, FaBatteryHalf, FaBatteryQuarter, FaBatteryThreeQuarters } from 'react-icons/fa';
import { FiAlertCircle, FiRefreshCcw, FiSearch, FiWifiOff } from 'react-icons/fi';
import { MdSignalWifiStatusbar4Bar, MdSignalWifiStatusbarNull } from 'react-icons/md';
import PageTitle from '../../../../components/PageTitle';
import api from '../../../../services/api';
import DeviceModal from './DeviceModal';
import * as S from './styles';

export interface Device {
  id: string;
  name: string;
  client?: string;
  batteryLevel?: number;
  signalStrength?: number;
  isOnline: boolean;
  lastSeen?: string;
  macAddress?: string;
}

interface SimCardInfo {
  slot?: string;
  simcard_num?: string;
  operadora?: string;
  sinal_operadora_dbm?: number;
}

interface SensorInfo {
  channel?: string;
  type?: string;
  model?: string;
}

interface BottomUpPayload {
  ver?: string;
  telecom?: {
    simcard_num_1?: string;
    sinal_operadora_1_dbm?: number;
  };
  leituras?: {
    contador_pulsos?: number;
    porta_analogica_1?: number;
    porta_analogica_2?: number;
  };
  data_envio?: string;
  diagnostico?: {
    status_bateria?: {
      tensao_mv?: number;
    };
  };
  numero_serie?: string;
}

export interface DeviceStatusSection {
  dispositivo?: {
    numero_serie?: string;
    fabricante?: string;
    modelo?: string;
    mac?: string;
    mac_address?: string;
  };
  comunicacao?: {
    imei1?: SimCardInfo;
    imei2?: SimCardInfo;
    sim_em_uso?: string;
    sinal_sim_ativo_dbm?: number;
  };
  portas?: {
    portas_analogicas_ativas?: number;
    portas_digitais_ativas?: number;
    sensores_conectados?: SensorInfo[];
  };
  coleta?: {
    periodo_varredura?: number;
    periodo_envio?: number;
  };
  bateria?: {
    tensao?: number;
    carga_percentual?: number;
    versao_firmware?: string;
    data_hora_ultima_conexao?: string;
    data_hora_envio?: string;
  };
  instalacao?: {
    localizacao?: string;
    coordenadas?: {
      lat?: string;
      lon?: string;
    };
    data_instalacao?: string;
    firmware?: string;
    hardware?: string;
  };
}

export interface DeviceStatusPayload {
  status?: DeviceStatusSection;
  isBottomUp?: boolean;
}

interface DeviceStatusApiItem extends DeviceStatusPayload {
  device_id?: string | number;
  macAddress?: string;
  serial?: string;
  id?: string | number;
  payload?: DeviceStatusPayload | BottomUpPayload;
}

type DeviceStatusMap = Record<string, DeviceStatusPayload>;

const DEVICE_STATUS_ENDPOINT = '/devices/monitor';

const normalizeIdentifier = (value?: string | number | null) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
};

const isBottomUpPayload = (value: unknown): value is BottomUpPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as BottomUpPayload;
  return Boolean(
    payload.ver
    || payload.numero_serie
    || payload.data_envio
    || payload.telecom
    || payload.leituras
    || payload.diagnostico,
  );
};

const countAnalogPorts = (leituras?: BottomUpPayload['leituras']) => {
  if (!leituras) {
    return 0;
  }

  const analogKeys: Array<keyof NonNullable<BottomUpPayload['leituras']>> = ['porta_analogica_1', 'porta_analogica_2'];
  const total = analogKeys.reduce((count, key) => {
    return leituras[key] !== undefined && leituras[key] !== 2 ? count + 1 : count;
  }, 0);

  return total;
};

const countDigitalPorts = (leituras?: BottomUpPayload['leituras']) => {
  if (!leituras) {
    return 0;
  }

  let count = 0;
  if (leituras.contador_pulsos !== undefined && leituras.contador_pulsos !== 0) {
    count += 1;
  }

  return count;
};

const buildBottomUpSensors = (leituras?: BottomUpPayload['leituras']): SensorInfo[] | undefined => {
  if (!leituras) {
    return undefined;
  }

  const sensors: SensorInfo[] = [];

  if (leituras.contador_pulsos !== undefined && leituras.contador_pulsos !== 0) {
    sensors.push({ channel: 'contador_pulsos', type: 'Contador de pulsos' });
  }

  if (leituras.porta_analogica_1 !== undefined && leituras.porta_analogica_1 !== 2) {
    sensors.push({ channel: '1', type: 'Porta analógica 1' });
  }

  if (leituras.porta_analogica_2 !== undefined && leituras.porta_analogica_2 !== 2) {
    sensors.push({ channel: '2', type: 'Porta analógica 2' });
  }

  return sensors.length ? sensors : undefined;
};

const mapBottomUpToStatusSection = (payload: BottomUpPayload): DeviceStatusSection => {
  const batteryVoltageMv = payload.diagnostico?.status_bateria?.tensao_mv;

  return {
    dispositivo: {
      numero_serie: payload.numero_serie,
      mac: payload.numero_serie,
      mac_address: payload.numero_serie,
      modelo: 'SW8 Pro',
    },
    comunicacao: {
      imei1: {
        slot: '1',
        simcard_num: payload.telecom?.simcard_num_1,
        sinal_operadora_dbm: payload.telecom?.sinal_operadora_1_dbm,
      },
      sim_em_uso: payload.telecom?.simcard_num_1 ? '1' : undefined,
      sinal_sim_ativo_dbm: payload.telecom?.sinal_operadora_1_dbm,
    },
    portas: {
      portas_analogicas_ativas: countAnalogPorts(payload.leituras),
      portas_digitais_ativas: countDigitalPorts(payload.leituras),
      sensores_conectados: buildBottomUpSensors(payload.leituras),
    },
    bateria: {
      tensao: batteryVoltageMv !== undefined ? batteryVoltageMv / 1000 : undefined,
      versao_firmware: payload.ver,
      data_hora_envio: payload.data_envio,
      data_hora_ultima_conexao: payload.data_envio,
    },
  };
};

const extractStatusKeys = (item: DeviceStatusApiItem, statusSection?: DeviceStatusSection): string[] => {
  const keys = [
    item.device_id,
    item.macAddress,
    item.serial,
    item.id,
    statusSection?.dispositivo?.numero_serie,
    statusSection?.dispositivo?.mac,
    statusSection?.dispositivo?.mac_address,
  ].map(normalizeIdentifier).filter(Boolean);
  return Array.from(new Set(keys));
};

const getStatusPayload = (item: DeviceStatusApiItem): DeviceStatusPayload => {
  const payloadSource = item.payload;
  const explicitStatus = payloadSource && 'status' in payloadSource
    ? payloadSource.status
    : item.status;

  if (isBottomUpPayload(payloadSource)) {
    return {
      status: mapBottomUpToStatusSection(payloadSource),
      isBottomUp: true,
    };
  }

  if (isBottomUpPayload(item)) {
    return {
      status: mapBottomUpToStatusSection(item),
      isBottomUp: true,
    };
  }

  if (explicitStatus && Object.keys(explicitStatus).length > 0) {
    return {
      status: explicitStatus as DeviceStatusSection,
      isBottomUp: false,
    };
  }

  return {
    status: {},
    isBottomUp: false,
  };
};

const getDeviceLookupKeys = (device: Device): string[] => {
  const keys = [
    device.macAddress,
    device.id,
    device.name,
  ].map(normalizeIdentifier).filter(Boolean);
  return Array.from(new Set(keys));
};

const DevicesMonitor: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceStatusMap, setDeviceStatusMap] = useState<DeviceStatusMap>({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [isStatusStale, setIsStatusStale] = useState(false);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data } = await api.get('/devices');
        
        if (!Array.isArray(data)) {
          setError('Formato de resposta da API inválido');
          return;
        }
        
        const apiDevices: Device[] = data.map((apiDevice: any, index: number) => ({
          id: apiDevice.id.toString(),
          name: apiDevice.name || `Device ${apiDevice.id}`,
          client: apiDevice.client?.name || 'Cliente não informado',
          batteryLevel: apiDevice.batteryLevel ?? apiDevice.battery_level ?? 0,
          signalStrength: apiDevice.signalStrength || apiDevice.signal_strength || Math.floor(Math.random() * 5),
          isOnline: apiDevice.isOnline !== undefined ? apiDevice.isOnline : (apiDevice.status === 'online' || Math.random() > 0.3),
          lastSeen: apiDevice.updated_at || apiDevice.lastSeen || apiDevice.last_seen || new Date().toISOString(),
          macAddress: apiDevice.macAddress || apiDevice.mac_address || `AA:BB:CC:DD:EE:${String(index).padStart(2, '0')}`,
        }));
        
        setDevices(apiDevices);
      } catch (err) {
        const error = err as any;
        setError(`Erro ao carregar dispositivos: ${error.response?.status || error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
  }, []);

  const refreshDeviceStatuses = useCallback(async () => {
    if (!devices.length) {
      return;
    }

    const deviceLookup = new Map<string, Device>();
    devices.forEach((device) => {
      getDeviceLookupKeys(device).forEach((key) => {
        if (key) {
          deviceLookup.set(key, device);
        }
      });
    });

    setStatusLoading(true);
    try {
      const { data } = await api.get<DeviceStatusApiItem[] | DeviceStatusApiItem>(DEVICE_STATUS_ENDPOINT);
      const payloadArray = Array.isArray(data) ? data : data ? [data] : [];
      const nextMap: DeviceStatusMap = {};

      payloadArray.forEach((item) => {
        if (!item || typeof item !== 'object') {
          return;
        }

        const payload = getStatusPayload(item);
        const statusSection = payload.status || {};

        const keys = extractStatusKeys(item, statusSection);
        const serialKey = payload.status?.dispositivo?.numero_serie
          ? normalizeIdentifier(payload.status.dispositivo.numero_serie)
          : '';

        if (serialKey) {
          keys.push(serialKey);
        }

        const matchedDevice = serialKey && deviceLookup.get(serialKey)
          ? deviceLookup.get(serialKey)
          : keys.map((key) => deviceLookup.get(key)).find(Boolean);

        if (matchedDevice) {
          keys.push(...getDeviceLookupKeys(matchedDevice));
        }

        const uniqueKeys = Array.from(new Set(keys));
        uniqueKeys.forEach((key) => {
          if (key) {
            nextMap[key] = payload;
          }
        });
      });

      setDeviceStatusMap(nextMap);
      setIsStatusStale(false);
    } catch (err) {
      console.error('Erro ao atualizar status dos dispositivos', err);
      setIsStatusStale(true);
    } finally {
      setStatusLoading(false);
    }
  }, [devices]);

  useEffect(() => {
    refreshDeviceStatuses();
  }, [refreshDeviceStatuses]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshDeviceStatuses();
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshDeviceStatuses]);

  const getStatusForDevice = useCallback((device: Device) => {
    const keys = getDeviceLookupKeys(device);
    for (const key of keys) {
      const payload = deviceStatusMap[key];
      if (payload) {
        return payload;
      }
    }
    return undefined;
  }, [deviceStatusMap]);

  const hasStatusForDevice = useCallback((device: Device) => {
    return Boolean(getStatusForDevice(device));
  }, [getStatusForDevice]);

  const devicesWithMQTT: Device[] = devices;

  useEffect(() => {
    const filtered = devicesWithMQTT
      .filter(device => device.name.toLowerCase().includes(nameFilter.toLowerCase()))
      .sort((a, b) => {
        const aHasStatus = hasStatusForDevice(a);
        const bHasStatus = hasStatusForDevice(b);

        if (aHasStatus === bHasStatus) {
          return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
        }

        return aHasStatus ? -1 : 1;
      });

    setFilteredDevices(filtered);
  }, [devicesWithMQTT, nameFilter, hasStatusForDevice]);

  const getBatteryColor = (batteryLevel?: number) => {
    if (!batteryLevel) return '#6c757d';
    if (batteryLevel > 60) return '#28a745';
    if (batteryLevel > 30) return '#ffc107';
    return '#dc3545';
  };

  const getBatteryIcon = (batteryLevel?: number) => {
    if (batteryLevel === undefined || batteryLevel === null) return FaBatteryEmpty;
    if (batteryLevel >= 80) return FaBatteryFull;
    if (batteryLevel >= 60) return FaBatteryThreeQuarters;
    if (batteryLevel >= 40) return FaBatteryHalf;
    if (batteryLevel >= 20) return FaBatteryQuarter;
    return FaBatteryEmpty;
  };

  const getSignalIcon = (signalStrength?: number, isOnline?: boolean) => {
    if (!isOnline) return FiWifiOff;
    if (!signalStrength || signalStrength <= 2) return MdSignalWifiStatusbarNull;
    return MdSignalWifiStatusbar4Bar;
  };

  const handleDeviceClick = (device: Device) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDevice(null);
  };

  const selectedDeviceStatus = selectedDevice ? getStatusForDevice(selectedDevice) : undefined;


  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return 'Sem registro';
    const date = new Date(lastSeen);
    if (Number.isNaN(date.getTime())) {
      return 'Sem registro';
    }
    return date.toLocaleString('pt-BR');
  };

  return (
    <S.Container>
      <PageTitle title="Monitor de Dispositivos" sub="Status dos dispositivos" />

      <S.FilterSection>
        <S.SearchContainer>
          <S.SearchInput
            type="text"
            placeholder="Buscar dispositivo por nome..."
            value={nameFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameFilter(e.target.value)}
          />
          <S.SearchIcon>
            <FiSearch size={18} />
          </S.SearchIcon>
        </S.SearchContainer>
        <S.FilterActions>
          <S.DeviceCount>
            {filteredDevices.length} dispositivo{filteredDevices.length !== 1 ? 's' : ''} encontrado{filteredDevices.length !== 1 ? 's' : ''}
          </S.DeviceCount>
          <S.RefreshButton
            type="button"
            onClick={refreshDeviceStatuses}
            disabled={statusLoading}
            aria-label="Atualizar status dos dispositivos"
            $isLoading={statusLoading}
          >
            <FiRefreshCcw size={16} />
            {statusLoading ? 'Atualizando...' : 'Atualizar status'}
          </S.RefreshButton>
        </S.FilterActions>
      </S.FilterSection>

      <S.ScrollableContent>
        {loading ? (
          <S.LoadingContainer>
            <S.LoadingSpinner />
            <p>Carregando dispositivos...</p>
          </S.LoadingContainer>
        ) : error ? (
          <S.EmptyState>
            <FiSearch size={48} color="#dc3545" />
            <h3>Erro ao carregar dispositivos</h3>
            <p>{error}</p>
          </S.EmptyState>
        ) : (
          <S.DevicesGrid>
            {filteredDevices.map((device) => {
              const statusPayload = getStatusForDevice(device);
              const statusBatteryLevel = statusPayload?.status?.bateria?.carga_percentual;
              const statusLastSeen = statusPayload?.status?.bateria?.data_hora_envio || statusPayload?.status?.bateria?.data_hora_ultima_conexao;
              const batteryLevel = statusBatteryLevel ?? device.batteryLevel;
              const lastSeen = statusLastSeen ?? device.lastSeen;
              const BatteryIcon = getBatteryIcon(batteryLevel);
              const hasStatusPayload = Boolean(statusPayload);
              const isDisabled = !hasStatusPayload;
              const shouldShowStatusWarning = isStatusStale || !hasStatusPayload;
              const badgeTitle = isStatusStale ? 'Dados desatualizados. Última tentativa falhou.' : 'Não atualizado';
              
              return (
                <S.DeviceCard
                  key={device.id}
                  onClick={() => handleDeviceClick(device)}
                  $batteryColor={getBatteryColor(batteryLevel)}
                  $isOnline={device.isOnline}
                  $isDisabled={isDisabled}
                >
                  {shouldShowStatusWarning && (
                    <S.StatusMissingBadge title={badgeTitle}>
                      <FiAlertCircle size={14} />
                    </S.StatusMissingBadge>
                  )}
                  <S.DeviceHeader>
                    <S.DeviceName>{device.name}</S.DeviceName>
                  </S.DeviceHeader>
                  
                  <S.DeviceClient>{device.client || 'Cliente não informado'}</S.DeviceClient>
                  
                  <S.DeviceMetrics>
                    <S.MetricItem>
                      <BatteryIcon size={16} color={getBatteryColor(batteryLevel)} />
                      <span>{batteryLevel ?? 0}%</span>
                    </S.MetricItem>
                    
                  </S.DeviceMetrics>
                  
                  <S.LastSeen>
                    Última atividade: {formatLastSeen(lastSeen)}
                  </S.LastSeen>
                </S.DeviceCard>
              );
            })}
          </S.DevicesGrid>
        )}

        {filteredDevices.length === 0 && !loading && !error && (
          <S.EmptyState>
            <FiSearch size={48} color="#6c757d" />
            <h3>Nenhum dispositivo encontrado</h3>
            <p>Tente ajustar os filtros de busca</p>
          </S.EmptyState>
        )}
      </S.ScrollableContent>

      {selectedDevice && (
        <DeviceModal
          device={selectedDevice}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          statusData={selectedDeviceStatus}
          statusLoading={statusLoading}
        />
      )}
    </S.Container>
  );
};

export default DevicesMonitor;